/**
 * # Underlay
 *
 * @example
 * new Spark.Underlay();
 *
 * @module components/underlay.js
 * @since 3.3.0
 */

import BaseComponent from './base';

import addClass from '../helpers/dom/add-class';
import hasClass from '../helpers/dom/has-class';
import makeElement from '../helpers/dom/make-element';
import removeClass from '../helpers/dom/remove-class';
import toggleClass from '../helpers/dom/toggle-class';

import mixin from '../helpers/util/mixin';

import events from '../mixins/events';

/**
 * Underlay
 * @class creates an Underlay
 */
class Underlay extends BaseComponent {

  static instance;

  /**
   * Underlay constructor.
   * @param {Object} params
   * @constructor
   */
  constructor(params = {}) {

    if (Underlay.instance) {
      Underlay.instance.config = params;
      return Underlay.instance;
    } else {
      super(undefined, params);

      this._cacheElements();
      this._bindEventListenerCallbacks();
      this._addEventListeners();

      this.style = params.style ? params.style : 'dark';
      if (params.interactive !== undefined) this.interactive = params.interactive;
      if (params.display !== undefined) this.display = params.display;

      // Temporarily fixing `animate` to `false`
      // if (params.animate !== undefined) this.animate = params.animate;
      this.animate = false;
    }

  }


  /**
   * Sets up references
   * @private
   */
  _cacheElements() {
    Underlay.instance = this;
    this.el = document.getElementById('spark-underlay');
    if (!this.el) {
      this.el = makeElement('<div id="spark-underlay" data-interactive hidden></div>');
      document.body.appendChild(this.el);
    }
  }


  /**
   * Create bound versions of event listener callbacks and store them.
   * Otherwise we can't unbind from these events later because the
   * function signatures won't match.
   * @private
   */
  _bindEventListenerCallbacks() {
    this._onMouseDownBound = this._onMouseDown.bind(this);
    this._onTouchMoveBound = this._onTouchMove.bind(this);
    this._onClickBound = this._onClick.bind(this);
    this._onDismissBound = this._onDismiss.bind(this);
  }


  /**
   * Add event listeners for DOM events.
   * @private
   */
  _addEventListeners() {
    this.el.addEventListener('mousedown', this._onMouseDownBound, true);
    this.el.addEventListener('touchmove', this._onTouchMoveBound, true);
    this.el.addEventListener('click', this._onClickBound, true);
    this.el.addEventListener('dismiss', this._onDismissBound, true);
  }


  /**
   * Remove event listeners for DOM events..
   * @private
   */
  _removeEventListeners() {
    this.el.removeEventListener('mousedown', this._onMouseDownBound, true);
    this.el.removeEventListener('touchmove', this._onTouchMoveBound, true);
    this.el.removeEventListener('click', this._onClickBound, true);
    this.el.removeEventListener('dismiss', this._onDismissBound, true);
  }


  /**
   * On Mouse Down
   * @param {Object} e
   * @private
   */
  _onMouseDown(e) {
    if (!this.interactive) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
  }


  /**
   * On Touch Move, prevent scrolling
   * @param {Object} e
   * @private
   */
  _onTouchMove(e) {
    if (hasClass(document.body, 'spark-no-scroll')) {
      e.preventDefault();
    }
  }


  /**
   * On Click
   * @param {Object} e
   * @private
   */
  _onClick(e) {
    if (!this.interactive) {
      e.preventDefault();
      e.stopPropagation();
    } else {
      this.trigger('dismiss');
    }
  }


  /**
   * On Dismiss
   * @private
   */
  _onDismiss() {
    this.hide().then(() => {
      Underlay.instance.trigger('change', {display: Underlay.instance.display});
    }).catch(() => {});
  }


  /**
   * Re-configure Underlay
   * @param {Object} params
   * @return {Promise}
   */
  configure(params = {}) {
    return new Promise((resolve, reject) => {
      if (params.animate !== undefined) Underlay.instance.animate = params.animate;
      if (params.interactive !== undefined) Underlay.instance.interactive = params.interactive;
      if (params.display !== undefined && params.style !== Underlay.instance.style) {
        if (params.display) {
          Underlay.instance.style = params.style;
          Underlay.instance.show().then(
            resolve
          ).catch(() => {
            reject(new Error('Underlay not visible'));
          });
        } else {
          Underlay.instance.hide().then(() => {
            Underlay.instance.style = params.style;
            resolve();
          }).catch(() => {
            reject(new Error('Underlay is visible'));
          });
        }
      } else {
        if (params.style) Underlay.instance.style = params.style;
        if (params.display !== undefined && params.display === false) {
          Underlay.instance.hide().then(
            resolve
          ).catch(() => {
            reject(new Error('Underlay is visible'));
          });
        } else if (params.display === true) {
          Underlay.instance.show().then(
            resolve
          ).catch(() => {
            reject(new Error('Underlay not visible'));
          });
        }
        resolve();
      }
    });
  }


  /**
   * setter for configuration
   * @param {Object} param
   */
  set config(params = {}) {
    this.configure(params).catch(() => {});
  }


  /**
   * getter for current configuration
   * @return {Object}
   */
  get config() {
    return {
      animate: this.animate,
      display: this.display,
      interactive: this.interactive,
      style: this.style
    };
  }


  /**
   * show Underlay
   * @param {Object} options
   * @return {Promise}
   */
  show(options = {}) {
    return new Promise((resolve, reject) => {
      if (!Underlay.instance.el.hidden && (hasClass(Underlay.instance.el, 'no-animate') || !hasClass(Underlay.instance.el, 'hide'))) {
        resolve();
      } else {
        Underlay.instance.trigger('reveal');

        if (options.animate !== undefined) Underlay.instance.animate = options.animate;

        if (Underlay.instance.animate) {
          Underlay.instance.el.addEventListener('animationend', function onVisible(e) {
            Underlay.instance.el.removeEventListener('animationend', onVisible);
            if (e.animationName === 'reveal-underlay') {
              Underlay.instance.trigger('show');
              resolve();
            } else {
              reject(new Error('Underlay was hidden'));
            }
          });
        }

        if (options.style) Underlay.instance.style = options.style;
        if (options.interactive !== undefined) Underlay.instance.interactive = options.interactive;
        addClass(document.body, 'spark-no-scroll');
        addClass(document.body, 'spark-no-select');
        addClass(document.body, 'spark-underlay-open');
        Underlay.instance.el.hidden = false;

        if (!Underlay.instance.animate) {
          Underlay.instance.trigger('show');
          resolve();
        }
      }
    });
  }


  /**
   * hide Underlay
   * @return {Promise}
   */
  hide() {
    return new Promise((resolve, reject) => {
      if (Underlay.instance.el.hidden || hasClass(Underlay.instance.el, 'hide')) {
        resolve();
      } else {
        if (Underlay.instance.animate) {
          Underlay.instance.el.addEventListener('animationend', function onHidden(e) {
            Underlay.instance.el.removeEventListener('animationend', onHidden);
            if (e.animationName === 'dismiss-underlay') {
              removeClass(document.body, 'spark-no-scroll');
              removeClass(document.body, 'spark-no-select');
              Underlay.instance.el.hidden = true;
              removeClass(Underlay.instance.el, 'hide');
              removeClass(document.body, 'spark-underlay-open');
              Underlay.instance.trigger('hide');
              resolve();
            } else {
              reject(new Error('Underlay was shown'));
            }
          });
          addClass(Underlay.instance.el, 'hide');
        } else {
          removeClass(document.body, 'spark-no-scroll');
          removeClass(document.body, 'spark-no-select');
          removeClass(document.body, 'spark-underlay-open');
          Underlay.instance.el.hidden = true;
          Underlay.instance.trigger('hide');
          resolve();
        }
      }
    });
  }


  /**
   * get current animation config status
   * @return {Boolean}
   */
  get animate() {
    return !hasClass(this.el, 'no-animate');
  }


  /**
   * enable or disable animations
   * @param {Boolean} animation
   */
  set animate(animation) {

    // Temporarily fix `animate` to `false`
    animation = false;

    toggleClass(this.el, 'no-animate', !animation);
  }


  /**
   * getter for current display value
   * @return {Boolean}
   */
  get display() {
    return !this.el.hidden;
  }


  /**
   * setter for display
   * @param {Boolean} visible
   */
  set display(visible) {
    if (visible && this.el.hidden) {
      this.show().then(() => {
        Underlay.instance.trigger('change', {display: true});
      }).catch(() => {});
    } else if (!this.el.hidden) {
      this.hide().then(() => {
        Underlay.instance.trigger('change', {display: false});
      }).catch(() => {});
    }
  }


  /**
   * Disable Underlay interactions, disable when you want to force user to resolve popups
   * @return {Promise}
   */
  disable() {
    if (Underlay.instance.el.hasAttribute('data-interactive')) {
      Underlay.instance.el.removeAttribute('data-interactive');
      Underlay.instance.trigger('change', {interactive: false});
    }
    return Promise.resolve();
  }


  /**
   * Enable Underlay, enable when you want underlay to respond to user interactions, and dismiss content
   * @return {Promise}
   */
  enable() {
    if (!Underlay.instance.el.hasAttribute('data-interactive')) {
      Underlay.instance.el.attributes.setNamedItem(document.createAttribute('data-interactive'));
      Underlay.instance.trigger('change', {interactive: true});
    }
    return Promise.resolve();
  }


  /**
   * getter for current interactive state
   * @return {Boolean}
   */
  get interactive() {
    return this.el.hasAttribute('data-interactive');
  }


  /**
   * setter for current disabled state
   * @param {Boolean} active
   */
  set interactive(active) {
    if (active) {
      this.enable();
    } else {
      this.disable();
    }
  }


  /**
   * Retrieve the current style name
   * @return {String}
   */
  get style() {
    return this.el.dataset.style || 'dark';
  }


  /**
   * set or clear the current style name
   * @param {String} name
   */
  set style(name) {
    if (name && name !== this.el.dataset.style) {
      this.el.dataset.style = name;
      Underlay.instance.trigger('change', {style: name});
    } else {
      this.el.removeAttribute('data-style');
    }

  }

  /**
   * Returns whether or not the underlay is currently visible
   * @return {Boolean}
   */
  get active() {
    return window.getComputedStyle(Underlay.instance.el).display === 'block';
  }

}

mixin(Underlay.prototype, events);

export default Underlay;
